import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { genericRetryStrategy } from './retry-strategy';

import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, retryWhen, tap } from 'rxjs/operators';
import { PluginForm } from '../services/dtos/pluginForm';
import { PluginFileSettingWithContent } from './dtos/pluginFileSettingWithContent';
import { PluginWithSettings } from './dtos/pluginWithSettings';
// eslint-disable-next-line max-len
import { LaboratorySpecificSetting } from '../modules/admin/components/shared/components/laboratory-specific-settings/models/laboratory-specific-setting.model';

@Injectable({ providedIn: 'root' })
export class PluginsService {
  private pluginsUrl = this.baseUrl + '/api/plugins';

  public constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    @Inject('API_BASE_URL') private baseUrl: string) { }

  public getAllBasicInfo(): Observable<PluginWithSettings[]> {
    return this.http.get<PluginWithSettings[]>(this.pluginsUrl + '/GetAllBasicInfo')
      .pipe(
        retryWhen(genericRetryStrategy()),
      );
  }

  public getAllPluginsForCustomer(id: string): Observable<PluginWithSettings[]> {
    return this.http.get<PluginWithSettings[]>(this.pluginsUrl + '/GetAllPluginsForCustomer/' + id)
      .pipe(
        retryWhen(genericRetryStrategy()),
      );
  }

  public getPlugin(id: string): Observable<PluginWithSettings> {
    return this.http.get<PluginWithSettings>(this.pluginsUrl + '/Get/' + id)
      .pipe(
        catchError(this.handleError<PluginWithSettings>('getPlugin'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public startPlugin(pluginId: string): Observable<any> {
    return this.http.put(this.pluginsUrl + '/Start/' + pluginId, null)
      .pipe(
        tap(() => this.toastr.success('Plugin Started Sucessfully')),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError(this.handleError<any>('startPlugin'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public stopPlugin(pluginId: string): Observable<any> {
    return this.http.put(this.pluginsUrl + '/Stop/' + pluginId, null)
      .pipe(
        tap(() => this.toastr.success('Plugin Stopped Sucessfully')),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError(this.handleError<any>('stopPlugin'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public enablePluginTestMode(pluginId: string): Observable<any> {
    return this.http.put(this.pluginsUrl + '/EnableTestMode/' + pluginId, null)
      .pipe(
        tap(() => this.toastr.success('Enabled Plugin Test Mode Sucessfully')),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError(this.handleError<any>('enablePluginTestMode'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updatePlugin(pluginToUpdate: PluginForm): Observable<any> {
    return this.http.put(this.pluginsUrl + '/Update/' + pluginToUpdate.id, pluginToUpdate)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<PluginForm>('updatePlugin'))
      );
  }

  public fetchDefaultConfigurationFile(id: string, key: string): void {
    this.http.get(this.pluginsUrl + '/GetDefaultConfigurationFile/' + id + '/' + key, { responseType: 'blob', observe: 'response' })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  .subscribe((response: any) => {
        const headers = response.headers;
        const contentDisposition = headers.get('content-disposition').split('filename=')[1].split(';')[0];
        const blob = new Blob([response.body], { type: response.type });
        const link = document.createElement('a');
        link.setAttribute('href', window.URL.createObjectURL(blob));
        link.setAttribute('download', contentDisposition ?? 'defaultFileName.txt');
        link.click();
      },
        catchError(this.handleError<Blob>('fetchDefaultConfigurationFile'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updatePluginConfigurationFile(pluginId: string, pluginConfigurationFile: PluginFileSettingWithContent): Observable<any> {
    const formData = new FormData();
    if (pluginConfigurationFile.content) {
      formData.append('content', pluginConfigurationFile.content, pluginConfigurationFile.fileName);
    }
    formData.append('key', pluginConfigurationFile.key);
    formData.append('id', pluginId);
    return this.http.put(this.pluginsUrl + '/UpdatePluginConfigurationFile/', formData)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<FormData>('updatePluginConfigurationFile'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public loadPluginLibrary(fileToUpload: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('pluginLibrary', fileToUpload, fileToUpload.name);

    return this.http.post(this.pluginsUrl + '/LoadPluginLibrary', formData)
      .pipe(
        tap(() => this.toastr.success('Plugin Library Loaded Sucessfully')),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError(this.handleError<any>('loadPluginLibrary'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deletePlugin(pluginId: string): Observable<any> {
    return this.http.delete(this.pluginsUrl + '/DeletePluginLibrary/' + pluginId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<Plugin>('deletePlugin'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public addPluginLaboratorySpecificSetting(settings: LaboratorySpecificSetting): Observable<any> {
    return this.http.post(this.pluginsUrl + '/AddPluginLaboratorySpecificSetting/', settings)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<PluginForm>('addPluginLaboratorySpecificSetting'))
      );
  }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public updatePluginLaboratorySpecificSetting(settings: LaboratorySpecificSetting): Observable<any> {
      return this.http.post(this.pluginsUrl + '/UpdatePluginLaboratorySpecificSetting/', settings)
        .pipe(
          tap(() => this.toastr.success('Saved Sucessfully')),
          catchError(this.handleError<PluginForm>('updatePluginLaboratorySpecificSetting'))
        );
    }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      public deletePluginLaboratorySpecificSetting(pluginId: string, settingId: string): Observable<any> {
        return this.http.delete(this.pluginsUrl + '/DeletePluginLaboratorySpecificSetting/' + pluginId + `/` + settingId)
          .pipe(
            tap(() => this.toastr.success('Saved Sucessfully')),
            catchError(this.handleError<PluginForm>('deletePluginLaboratorySpecificSetting'))
          );
      }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private handleError<T>(operation = 'operation', result?: T | undefined): (err: any) => Observable<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error: any): Observable<T> => {
      console.error('An error occurred', error);
      this.toastr.error(`${operation} failed: ${error.message}`);

      throw error;
    };
  }
}
